// package
import React, { useState, useRef, useCallback, useEffect } from "react";
import { Col, Form } from "react-bootstrap";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";

// includes
import { config } from "includes";

const libraries: Array<"drawing" | "places" | "geometry"> = [
  "drawing",
  "places",
  "geometry",
];

const mapContainerStyle = { width: "100%", height: "400px" };
const center = { lat: 40.7128, lng: -74.006 };

// clickableIcons defaults to true, which makes Google's own place bubble open
// whenever a tap lands on a POI icon — that bubble is the "Place info couldn't
// load" tooltip, and it swallows the tap instead of dropping the pin. On a
// phone webview, where the map is small and a fingertip covers several icons,
// it is almost unavoidable. Incidents happen at coordinates, not at listed
// businesses, so the POI layer has nothing to offer this map.
const mapOptions: google.maps.MapOptions = {
  clickableIcons: false,
};

interface GoogleMapProps {
  onSuccess: (data: any) => void;
  latLng: { lat: number; lng: number };
  /** An address to look up and pin, supplied by something other than the user
   *  typing — currently the dispatch parser. Behaves as though the address had
   *  been chosen from the search box. */
  searchAddress?: string;
}

const GoogleMaps: React.FC<GoogleMapProps> = (props: any) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.mapApiKey,
    libraries,
  });

  const [marker, setMarker] = useState<{ lat: number; lng: number }>(
    props?.latLng?.lat && props?.latLng?.lng
      ? { lat: props.latLng.lat, lng: props.latLng.lng }
      : center
  );
  const [address, setAddress] = useState<string>("");
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [clickMarker] = useState(true);

  // Look up an address handed in from outside and pin it, so a parsed dispatch
  // lands on the map the same way a typed search would. The search box is
  // uncontrolled (Google's Autocomplete owns it), so its text is set directly.
  //
  // The address reported back is the one we were given, not the geocoder's
  // formatted version: a dispatch's own wording ("570 W 156th St off Amsterdam
  // Ave") carries cross-street detail that Google's tidy-up throws away. The
  // coordinates are the geocoder's — those we do want.
  useEffect(() => {
    const wanted = props.searchAddress;
    if (!isLoaded || !wanted) return;

    let live = true;
    (async () => {
      try {
        const geocoder = new window.google.maps.Geocoder();
        const res = await geocoder.geocode({ address: wanted });
        const hit = res.results[0];
        if (!live || !hit) return;

        const lat = hit.geometry.location.lat();
        const lng = hit.geometry.location.lng();
        setMarker({ lat, lng });
        setAddress(wanted);
        if (searchInputRef.current) searchInputRef.current.value = wanted;
        props?.onSuccess({ lat, lng, address: wanted });
      } catch {
        // A dispatch address Google can't place is not an error worth
        // interrupting the admin for — they can still drop the pin by hand.
      }
    })();

    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, props.searchAddress]);

  // Handle marker drag event
  const handleMarkerDragEnd = async (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const newLat = event.latLng.lat();
      const newLng = event.latLng.lng();
      setMarker({ lat: newLat, lng: newLng });
      await getAddressFromLatLng(newLat, newLng);
    }
  };

  // function
  const onClickMap = async (e: any) => {
    if (clickMarker) {
      let lat = e.latLng.lat();
      let lng = e.latLng.lng();
      setMarker({ lat, lng });
      await getAddressFromLatLng(lat, lng);
    }
  };

  // Fetch address based on latitude and longitude.
  //
  // Reports the lat/lng it was called with, never the `marker` state: the
  // setMarker() that precedes every call here does not apply until the next
  // render, so reading marker.lat/lng handed back the PREVIOUS pin's
  // coordinates paired with the new pin's address.
  const getAddressFromLatLng = async (lat: number, lng: number) => {
    const geocoder = new window.google.maps.Geocoder();
    const response = await geocoder.geocode({ location: { lat, lng } });
    const formatted = response.results[0]?.formatted_address;
    setAddress(formatted || "Address not found");
    // The pin is what the incident is required to carry; a spot the geocoder
    // can't name still has to record its coordinates.
    props?.onSuccess({ lat, lng, address: formatted || "" });
  };

  // Initialize autocomplete
  const onLoad = useCallback(
    (autocomplete: google.maps.places.Autocomplete) => {
      autocompleteRef.current = autocomplete;
    },
    []
  );

  // Handle place change from autocomplete
  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const location: any = place.geometry.location;
        const lat = location.lat();
        const lng = location.lng();
        setMarker({ lat, lng });
        setAddress(place.formatted_address || "");
        props?.onSuccess({
          lat: lat,
          lng: lng,
          address: place.formatted_address,
        });
      }
    }
  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps...</div>;

  return (
    <div>
      {marker.lat && marker.lng ? (
        <div>
          <div style={{ marginBottom: "10px" }}>
            <Col md={12}>
                <Form.Group className="mb-0 mt-3">
                <Form.Label>Type Address</Form.Label>
                  <Form.Control
                    type="text"
                    id="singleLine"
                    className="address-txt-box mb-3"
                    placeholder="Search for places..."
                    ref={(ref: any) => {
                      searchInputRef.current = ref;
                      if (ref && !autocompleteRef.current) {
                        const autocomplete =
                          new window.google.maps.places.Autocomplete(ref, {
                            fields: ["geometry", "formatted_address"],
                          });
                        autocomplete.addListener(
                          "place_changed",
                          onPlaceChanged
                        );
                        onLoad(autocomplete);
                      }
                    }}
                  />
              </Form.Group>
              <div className="map-wrap">
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={marker}
                  zoom={14}
                  onClick={onClickMap}
                  options={mapOptions}
                >
                  <Marker
                    position={marker}
                    draggable={true}
                    onDragEnd={handleMarkerDragEnd}
                  />
                </GoogleMap>
              </div>
            </Col>
          </div>
          {/* <div>
                <h4>Details</h4>
                <p><strong>Address:</strong> {address}</p>
                <p><strong>Latitude:</strong> {marker.lat}</p>
                <p><strong>Longitude:</strong> {marker.lng}</p>
            </div> */}
        </div>
      ) : (
        <div>Loading map...</div>
      )}
    </div>
  );

};

export default GoogleMaps;
